/**
 * Home Page / Landing Page
 *
 * Modern landing page with comprehensive feature showcase for the form builder platform.
 * Optimized for SEO and conversion with clear CTAs.
 */

import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../lib/seo';
import PublicLayout from '../components/layout/PublicLayout';

export default function HomePage() {
  const { t, i18n } = useTranslation('landing');

  // SEO: Update meta tags based on current language
  useSEO({
    title: t('meta.title'),
    description: t('meta.description'),
    keywords: t('meta.keywords', 'form builder, survey maker, quiz creator, live quiz, polls, free forms'),
    locale: i18n.language,
  });

  // SEO: Add hreflang tags for all supported languages
  useHreflang('/');

  // SEO: SoftwareApplication schema
  const softwareAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    'name': 'xtFORM',
    'applicationCategory': 'BusinessApplication',
    'operatingSystem': 'Web',
    'description': t('meta.description'),
    'url': SITE_CONFIG.domain,
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.8',
      'ratingCount': '150',
      'bestRating': '5',
      'worstRating': '1',
    },
    'featureList': [
      'Drag-and-drop form builder',
      '50+ field types',
      'Live quiz competitions',
      'Real-time analytics',
      'Team collaboration',
      'REST API',
      'AES-256 encryption',
      'GDPR compliant',
    ],
  };

  // SEO: HowTo schema for form creation
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How to Create a Form with xtFORM',
    'description': 'Learn how to create professional forms, surveys, and quizzes in minutes with xtFORM\'s drag-and-drop builder.',
    'step': [
      {
        '@type': 'HowToStep',
        'position': 1,
        'name': 'Create Your Form',
        'text': 'Sign up for free and use our drag-and-drop builder to design your form. Add fields, set validations, and customize the look.',
        'url': `${SITE_CONFIG.domain}/register`,
      },
      {
        '@type': 'HowToStep',
        'position': 2,
        'name': 'Share & Collect',
        'text': 'Publish your form and share it via link, embed it on your website, or integrate it through our API.',
        'url': `${SITE_CONFIG.domain}/docs/api`,
      },
      {
        '@type': 'HowToStep',
        'position': 3,
        'name': 'Analyze Results',
        'text': 'View submissions in real-time, export to CSV, JSON, or Excel, and integrate with your favorite tools.',
        'url': `${SITE_CONFIG.domain}/formspace`,
      },
    ],
    'totalTime': 'PT5M',
    'tool': {
      '@type': 'HowToTool',
      'name': 'xtFORM Form Builder',
    },
  };

  useStructuredData(softwareAppSchema, 'software-application');
  useStructuredData(howToSchema, 'how-to-create-form');

  // Update document title (keeping for SSR fallback)
  useEffect(() => {
    document.title = t('meta.title');
  }, [t]);

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-40 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
              </span>
              {t('hero.badge')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('hero.title')}
              <span className="text-blue-600"> {t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/25"
              >
                {t('hero.cta')}
              </Link>
              <Link
                to="/play"
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-600/25"
              >
                {t('hero.ctaSecondary')}
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">{t('hero.noCreditCard')}</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-blue-600">50+</div>
              <div className="text-gray-600 text-sm mt-1">{t('stats.fieldTypes')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-green-600">99.9%</div>
              <div className="text-gray-600 text-sm mt-1">{t('stats.uptime')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-purple-600">{t('stats.liveQuizzes')}</div>
              <div className="text-gray-600 text-sm mt-1">{t('stats.liveQuizzes')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-600">AES-256</div>
              <div className="text-gray-600 text-sm mt-1">{t('stats.encryption')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.dragDrop.title')}</h3>
              <p className="text-gray-600">
                {t('features.dragDrop.description')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.security.title')}</h3>
              <p className="text-gray-600">
                {t('features.security.description')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.api.title')}</h3>
              <p className="text-gray-600">
                {t('features.api.description')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.collaboration.title')}</h3>
              <p className="text-gray-600">
                {t('features.collaboration.description')}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-cyan-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.analytics.title')}</h3>
              <p className="text-gray-600">
                {t('features.analytics.description')}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('features.mobile.title')}</h3>
              <p className="text-gray-600">
                {t('features.mobile.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Quiz Section - NEW */}
      <section id="live-quiz" className="py-24 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full text-purple-300 text-sm font-medium mb-6">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('liveQuiz.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('liveQuiz.title')}
              </h2>
              <p className="text-xl text-purple-200 mb-8">
                {t('liveQuiz.subtitle')}
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3 text-purple-100">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('liveQuiz.features.realtime')}
                </li>
                <li className="flex items-center gap-3 text-purple-100">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('liveQuiz.features.leaderboard')}
                </li>
                <li className="flex items-center gap-3 text-purple-100">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('liveQuiz.features.scoring')}
                </li>
                <li className="flex items-center gap-3 text-purple-100">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('liveQuiz.features.hostControls')}
                </li>
                <li className="flex items-center gap-3 text-purple-100">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('liveQuiz.features.analytics')}
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition-colors text-center"
                >
                  {t('liveQuiz.cta')}
                </Link>
                <Link
                  to="/play"
                  className="px-6 py-3 bg-purple-500/20 text-white border border-purple-400/30 rounded-xl font-semibold hover:bg-purple-500/30 transition-colors text-center"
                >
                  {t('liveQuiz.ctaSecondary')}
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-center mb-6">
                  <p className="text-purple-300 text-sm uppercase tracking-wide">{t('liveQuiz.gamePin')}</p>
                  <p className="text-5xl font-mono font-bold text-white tracking-wider">847291</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-4 flex items-center gap-3">
                    <span className="text-2xl">1</span>
                    <span className="font-bold text-white flex-1">PlayerOne</span>
                    <span className="text-white font-mono">2,450 pts</span>
                  </div>
                  <div className="bg-gradient-to-r from-gray-400 to-gray-500 rounded-xl p-4 flex items-center gap-3">
                    <span className="text-2xl">2</span>
                    <span className="font-bold text-white flex-1">QuizMaster</span>
                    <span className="text-white font-mono">2,100 pts</span>
                  </div>
                  <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl p-4 flex items-center gap-3">
                    <span className="text-2xl">3</span>
                    <span className="font-bold text-white flex-1">BrainTeaser</span>
                    <span className="text-white font-mono">1,850 pts</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10 text-center">
                  <p className="text-purple-300 text-sm">{t('liveQuiz.playersCompeting', { count: 12 })}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Features Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('quizFeatures.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('quizFeatures.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('quizFeatures.autoGrading.title')}</h3>
              <p className="text-gray-600 text-sm">{t('quizFeatures.autoGrading.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('quizFeatures.timeLimits.title')}</h3>
              <p className="text-gray-600 text-sm">{t('quizFeatures.timeLimits.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('quizFeatures.retake.title')}</h3>
              <p className="text-gray-600 text-sm">{t('quizFeatures.retake.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('quizFeatures.results.title')}</h3>
              <p className="text-gray-600 text-sm">{t('quizFeatures.results.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('howItWorks.step1.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.step1.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('howItWorks.step2.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.step2.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('howItWorks.step3.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.step3.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WordPress Integration Section */}
      <section className="py-24 bg-gradient-to-br from-[#21759b]/5 via-white to-[#21759b]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#21759b]/10 rounded-full text-[#21759b] text-sm font-medium mb-6">
                <svg className="w-5 h-5" viewBox="0 0 256 255" fill="currentColor">
                  <path d="M18.124 127.5c0 43.296 25.16 80.711 61.646 98.442L27.594 82.986a108.965 108.965 0 0 0-9.47 44.514m183.221-5.52c0-13.517-4.856-22.879-9.02-30.165-5.545-9.01-10.742-16.64-10.742-25.65 0-10.055 7.626-19.415 18.368-19.415.485 0 .944.06 1.417.088-19.46-17.829-45.387-28.714-73.863-28.714-38.213 0-71.832 19.606-91.39 49.302 2.566.077 4.985.13 7.039.13 11.44 0 29.15-1.387 29.15-1.387 5.897-.348 6.592 8.32.702 9.014 0 0-5.926.697-12.52 1.042l39.832 118.479 23.94-71.737-17.042-46.742c-5.89-.345-11.47-1.042-11.47-1.042-5.898-.347-5.203-9.362.692-9.014 0 0 18.06 1.387 28.81 1.387 11.44 0 29.15-1.387 29.15-1.387 5.9-.348 6.594 8.32.703 9.014 0 0-5.938.697-12.52 1.042l39.529 117.581 10.91-36.458c4.728-15.129 8.327-25.995 8.327-35.36" />
                  <path d="M130.68 134.693 96.868 233.265c10.07 2.962 20.716 4.577 31.737 4.577 13.085 0 25.631-2.26 37.302-6.361a3.007 3.007 0 0 1-.24-.465l-34.986-96.323ZM234.687 73.608a89.311 89.311 0 0 1 .738 11.553c0 11.396-2.133 24.222-8.559 40.264L191.397 225.4c33.27-19.397 55.666-55.628 55.666-97.059 0-19.648-5.088-38.104-13.837-54.22" />
                  <path d="M128.385 0C57.505 0 0 57.29 0 127.918c0 70.614 57.505 127.918 128.385 127.918 70.874 0 128.385-57.304 128.385-127.918C256.77 57.29 199.259 0 128.385 0m0 250.081c-67.434 0-122.305-54.676-122.305-122.163S60.951 5.755 128.385 5.755c67.43 0 122.304 54.676 122.304 122.163S195.814 250.08 128.385 250.08" />
                </svg>
                {t('wordpress.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('wordpress.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t('wordpress.subtitle')}
              </p>
              <ul className="space-y-4 mb-8">
                {(['shortcode', 'submissions', 'nativeRender', 'autoUpdates', 'webhooks'] as const).map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-[#21759b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{t(`wordpress.features.${key}`)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://xt4m.com/api/wordpress-plugin/download"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#21759b] text-white rounded-xl font-semibold hover:bg-[#1a5f7f] transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('wordpress.cta')}
                </a>
                <Link
                  to="/blog/xtform-wordpress-plugin-embed-forms-surveys"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#21759b] text-[#21759b] rounded-xl font-semibold hover:bg-[#21759b]/5 transition-colors"
                >
                  {t('wordpress.ctaSecondary')}
                </Link>
              </div>
            </div>

            {/* Right — Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                {/* WP Admin Header Mock */}
                <div className="bg-[#1d2327] px-4 py-3 flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
                  </div>
                  <div className="text-gray-400 text-xs font-mono truncate">yoursite.com/wp-admin/admin.php?page=xtform</div>
                </div>
                {/* WP Admin Sidebar + Content Mock */}
                <div className="flex">
                  <div className="w-48 bg-[#23282d] text-gray-300 text-sm py-4 hidden sm:block">
                    <div className="px-4 py-2 text-gray-500 text-xs uppercase tracking-wider">xtFORM</div>
                    <div className="px-4 py-2 bg-[#0073aa] text-white">Forms</div>
                    <div className="px-4 py-2 hover:text-white">Submissions</div>
                    <div className="px-4 py-2 hover:text-white">Builder</div>
                    <div className="px-4 py-2 hover:text-white">Settings</div>
                  </div>
                  <div className="flex-1 p-6 bg-[#f1f1f1]">
                    <div className="text-lg font-semibold text-gray-800 mb-4">xtFORM Forms</div>
                    <div className="bg-white rounded border border-gray-200">
                      <div className="grid grid-cols-3 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-600 uppercase">
                        <span>Name</span>
                        <span>Status</span>
                        <span>Responses</span>
                      </div>
                      {[
                        { name: 'Contact Form', status: 'Published', count: 142 },
                        { name: 'Feedback Survey', status: 'Published', count: 89 },
                        { name: 'Event Registration', status: 'Draft', count: 0 },
                      ].map((form, i) => (
                        <div key={i} className="grid grid-cols-3 gap-4 px-4 py-3 border-b last:border-0 text-sm">
                          <span className="text-blue-600 font-medium">{form.name}</span>
                          <span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${form.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {form.status}
                            </span>
                          </span>
                          <span className="text-gray-600">{form.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - 100% Free */}
      <section id="pricing" className="py-24 bg-gray-50 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Free Forever Card */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 shadow-xl text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

              <div className="relative">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                    </svg>
                    {t('pricing.badge')}
                  </div>
                  <div className="text-6xl font-bold mb-2">{t('pricing.price')}</div>
                  <div className="text-green-100 text-lg">{t('pricing.period')}</div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.unlimitedForms')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.unlimitedResponses')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.liveQuizzes')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.leaderboards')}
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.teamCollaboration')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.export')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.apiAccess')}
                    </li>
                    <li className="flex items-center gap-2 text-white">
                      <svg className="w-5 h-5 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('pricing.features.allFieldTypes')}
                    </li>
                  </ul>
                </div>

                <Link
                  to="/register"
                  className="block w-full py-4 text-center bg-white text-green-600 rounded-xl font-semibold text-lg hover:bg-green-50 transition-colors"
                >
                  {t('pricing.cta')}
                </Link>
              </div>
            </div>

            {/* Future pricing disclaimer */}
            <p className="text-center text-xs text-gray-400 mt-6">
              {t('pricing.disclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-colors"
            >
              {t('cta.primary')}
            </Link>
            <Link
              to="/docs/api"
              className="px-8 py-4 bg-blue-500 text-white rounded-xl font-semibold text-lg hover:bg-blue-400 transition-colors"
            >
              {t('cta.secondary')}
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
